import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography
} from '@mui/material';
import { GroupsOutlinedIcon } from 'icons/materialIcons';
import { useNavigate } from 'react-router-dom';
import { useCreateTeamMember } from 'hooks/useCreateTeamMember';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamCreateCompanyPagesContent = ({ onDataChange, onValidationChange }) => {
    const [pageCount, setPageCount] = useState(0);
    const [isValid, setIsValid] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const { createCompanyPages, loading } = useCreateTeamMember();

    useEffect(() => {
        const valid = pageCount > 0 && pageCount <= 100;
        setIsValid(valid);
        onValidationChange(valid);

        // Create mock data for the number of pages
        const mockPages = Array.from({ length: pageCount }, (_, index) => ({
            id: index + 1,
            name: '',
            description: '',
            type: 'company'
        }));
        onDataChange(valid ? mockPages : []);
    }, [pageCount, onDataChange, onValidationChange]);

    const handlePageCountChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            setPageCount(0);
        } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
                setPageCount(Math.max(0, Math.min(100, numValue)));
            }
        }
    };

    const handleCreatePages = async () => {
        if (!isValid || loading) return;

        try {
            const response = await createCompanyPages(pageCount);
            const successCount = response?.successCount || response?.createdCount || pageCount;
            const failedCount = response?.failedCount || 0;

            if (failedCount > 0) {
                setSnackbarMessage(`Created ${successCount} company page(s). ${failedCount} failed.`);
                setSnackbarSeverity('warning');
            } else {
                setSnackbarMessage(`Created ${successCount} company page(s).`);
                setSnackbarSeverity('success');
            }
            setSnackbarOpen(true);

            setPageCount(0);
            onDataChange([]);
            onValidationChange(false);

            setTimeout(() => {
                navigate('/team/members');
            }, 1200);
        } catch (error) {
            setSnackbarMessage(error.message || 'Failed to create company pages');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    return (
        <>
            <Box sx={{
                padding: '2rem',
                paddingTop: '3rem',
                maxWidth: '700px' // Reduce content width
            }}>
            {/* Header */}
            <Typography variant="body2" sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '1.25rem'
            }}>
                Add Company Pages
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{
                marginBottom: '1.5rem',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#828282',
                lineHeight: 1.5
            }}>
                Create company pages if you want to share information that is NOT associated with a team member.
                <br /><br />
                For example, if you want to share information about your company from your conference booth, you could use a company page to do this. Any team member that is sharing a profile with information about themselves should be added via email invite, csv upload or AD sync.
            </Typography>

            {/* Page Count Input and Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2rem'
            }}>
                {/* Left: Page Count Input */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <TextField
                        type="number"
                        value={pageCount || ''}
                        onChange={handlePageCountChange}
                        placeholder="Number of pages"
                        sx={{
                            width: '150px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0.25rem',
                                backgroundColor: '#f7f7f7',
                                '& fieldset': {
                                    border: 'none',
                                },
                                '&:hover fieldset': {
                                    border: 'none',
                                },
                                '&.Mui-focused fieldset': {
                                    border: '0.125rem solid #1976d2',
                                    outline: 'none',
                                },
                                '& input': {
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.8rem',
                                    fontWeight: '300',
                                },
                                '& input::placeholder': {
                                    fontSize: '0.8rem',
                                    fontWeight: '300',
                                },
                            },
                        }}
                    />
                </Box>

                {/* Right: Add Pages Button */}
                <Button
                    variant="contained"
                    disabled={!isValid || loading}
                    onClick={handleCreatePages}
                    sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: '1.25rem',
                        textTransform: 'none',
                        padding: '0.5rem 1.5rem',
                        fontSize: '0.875rem',
                        minWidth: '8rem',
                        height: '2.5rem',
                        '&:hover': {
                            backgroundColor: '#333',
                        },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#999',
                        }
                    }}
                >
                    {loading ? 'Adding...' : 'Add Pages'}
                </Button>
            </Box>

            {/* Blue Summary Section */}
            {pageCount > 0 && (
                <Box sx={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#E3F2FD',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupsOutlinedIcon sx={{
                            color: '#1976d2',
                            fontSize: '1rem',
                            marginRight: '0.75rem'
                        }} />
                        <Typography variant="body1" sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#828282',
                            lineHeight: 1.5
                        }}>
                            Adding {pageCount} company page{pageCount !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#828282',
                        lineHeight: 1.5
                    }}>
                        This will influence your monthly payment
                    </Typography>
                </Box>
            )}
            </Box>
            <TeamNotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                handleClose={() => setSnackbarOpen(false)}
            />
        </>
    );
};

export default TeamCreateCompanyPagesContent;
